import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button, Chip, Dialog, DialogTitle, FormControl, Grid, IconButton, Input, InputAdornment, List, ListItem, ListItemButton, ListItemText, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { makeStyles } from "@mui/styles";
import { Add } from "@mui/icons-material";
import { debounce, get, identity, map, pickBy } from "lodash";
import clsx from "clsx";
import { useSnackbar } from "notistack";
import moment from "moment";
import { announcementTypeList, camelCaseToTitleCase, ERRORS_MESSAGE } from "../../constants";
import { announcementSelector, getAnnouncements, saveAnnouncementValue, startAnnouncement } from "../../store/slices/announcementSlice";
import { userSelector } from "../../store/slices/userSlice";
import DataGridTable from "../dataGridTable/DataGridTable";


const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  margin: {
    margin: theme.spacing(1),
  },
  withoutLabel: {
    marginTop: theme.spacing(3),
  },
  textField: {
    width: "61ch",
  },
}));

export function StartAnnouncementDialog({ open, handleClose, startAnnouncement, announcementTypeList, disabled }) {

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle>Select announcement type</DialogTitle>
      <List sx={{ pt: 0 }}>
        {announcementTypeList?.map((item) => (
          <ListItem key={item?.key}>
            <ListItemButton onClick={() => startAnnouncement(item?.value)} disabled={disabled}>
              <ListItemText primary={item?.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Dialog>
  );
}

const AnnouncementList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [searchTerm, setSearchTerm] = React.useState("");
  const user = useSelector(userSelector);
  const userId = user?._id;
  const { announcements, isGettingAnnouncements, isAnnouncementStarting, page } = useSelector(announcementSelector);
  const classes = useStyles();
  const [rows, setRows] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [params, setParams] = useState({ offset: 1 });

  const masterViewColumns = [
    {
      field: "title",
      headerName: "Title",
      type: "string",
      width: 450,
      editable: false,
    },
    {
      field: "type",
      headerName: "Type",
      type: "string",
      width: 150,
      editable: false,
      renderCell: (params) => (
        <Typography style={{ fontSize: "14px" }}>{camelCaseToTitleCase(params.value)}</Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      type: "string",
      width: 150,
      editable: false,
      renderCell: (params) => (
        <Chip label={params.value} color={params.value === "active" ? "success" : "error"} variant="outlined" size="small" />
      ),
    },
    {
      field: "createdAt",
      headerName: "Created At",
      type: "string",
      width: 120,
      editable: false,
      renderCell: (params) => (
        <div>
          <Typography style={{ fontSize: "14px" }}>{params.value.date}</Typography>
          {/* <Typography style={{ fontSize: "11px", color: "#999" }}>{params.value.time}</Typography> */}
        </div>
      ),
    },
    {
      field: "updatedAt",
      headerName: "Updated At",
      type: "string",
      width: 120,
      editable: false,
      renderCell: (params) => (
        <div>
          <Typography style={{ fontSize: "14px" }}>{params.value.date}</Typography>
          {/* <Typography style={{ fontSize: "11px", color: "#999" }}>{params.value.time}</Typography> */}
        </div>
      ),
    },
    {
      field: "action",
      headerName: "Action",
      width: 220,
      editable: false,
      // disableClickEventBubbling: gettingClonedApplication,
      renderCell: (params) => (
        <Button
          color="primary"
          variant="contained"
          size="small"
          // disabled={params?.id && gettingClonedApplication}
          onClick={(e) => {
            e.stopPropagation();
            handle.editAnnouncement(params?.id);
          }}
        >
          Edit
        </Button>
      ),
    },
  ];

  const handle = {
    getAllAnnouncementsFn: (cursor, search) => {
      if (userId) {
        dispatch(getAnnouncements({ userId, cursor, search }));
      }
    },
    handleAnnouncementSearch: (searchTerm) => {
      (searchTerm.length >= 2 || searchTerm.length <= 0) &&
        handle.getAllAnnouncementsFn(params?.offset, searchTerm);
    },
    handleClose: () => {
      setOpenDialog(false);
    },
    startAnnouncement: (announcementType) => {
      const data = {
        type: announcementType,
      }
      dispatch(startAnnouncement(data)).then((res) => {
        if (res?.error) {
          enqueueSnackbar(res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, {
            variant: "error",
            autoHideDuration: 5000,
          });

        } else {
          const data = res?.payload?.data?.data;
          navigate(`/announcement/${data?._id}`);
        }
      })
    },
    editAnnouncement: (announcementId) => {
      navigate(`/announcement/${announcementId}`);
    }
  };

  useEffect(() => {
    if (userId && params?.offset) {
      handle.getAllAnnouncementsFn(params?.offset, searchTerm);
      dispatch(saveAnnouncementValue({ page: params?.offset }));
    }
  }, [params?.offset, userId]);

  // Some API clients return undefined while loading
  // Following lines are here to prevent `rowCountState` from being undefined during the loading
  const [rowCountState, setRowCountState] = useState(announcements?.totalDocs || 0);

  useEffect(() => {
    setRowCountState((prevRowCountState) => announcements?.totalDocs ?? prevRowCountState);
  }, [announcements?.totalDocs]);

  useEffect(() => {
    const transformAnnouncement = (doc) => {

      const createdAt = new Date(get(doc, "createdAt"));
      const updatedAt = new Date(get(doc, "updatedAt"));

      return pickBy(
        {
          id: get(doc, "_id"),
          title: get(doc, "title"),
          type: get(doc, "type"),
          status: get(doc, "status"),
          createdAt: {
            date: moment(createdAt).format("D-M-YY"),
            // time: moment(createdAt).format("h:mm a"),
          },
          updatedAt: {
            date: moment(updatedAt).format("D-M-YY"),
            // time: moment(updatedAt).format("h:mm a"),
          },
        },
        identity,
      )
    }


    const transformedRows = map(get(announcements, "docs"), transformAnnouncement);
    setRows(transformedRows);
  }, [announcements]);

  const debounced = useCallback(debounce(handle.handleAnnouncementSearch, 500), []);

  return (
    <React.Fragment>
      <Grid container item direction="column" spacing={2} style={{ padding: "20px", minWidth: "99vw" }}>

        <Grid
          item
          style={{
            borderBottom: "1px solid rgba(224, 224, 224, 1)",
            display: "flex",
            alignItems: "center",
            columnGap: "40px",
          }}
        >
          <Typography
            variant="h1"
            style={{
              fontSize: "24px",
              fontWeight: 800,
              letterSpacing: "-0.5px",
            }}
          >
            Announcements
          </Typography>
          <div className={classes.root}>
            <FormControl className={clsx(classes.margin, classes.textField)}>
              <Input
                className="input-border-bottom"
                id="application-search"
                autoComplete={false}
                variant="filled"
                placeholder="Search…"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  debounced(e.target.value, 1000);
                }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                }
                endAdornment={
                  searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => {
                          setSearchTerm("");
                          searchTerm !== "" && debounced("", 1000);
                        }}
                        color="secondary"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }
              />
            </FormControl>
          </div>
          <Button
            variant="contained"
            disabled={isAnnouncementStarting}
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
          >
            ADD ANNOUNCEMENT
          </Button>
        </Grid>

        <Grid item sx={{ margin: "auto", width: "100%" }}>
          <DataGridTable
            data={rows || []}
            columns={masterViewColumns}
            onRowClick={(event) => {
              navigate(`/announcement/${event.id}`);
            }}
            rowsPerPageOptions={[10]}
            pageSize={10}
            rowCount={rowCountState}
            params={params}
            setParams={setParams}
            page={page}
            isLoading={isGettingAnnouncements}
          />
        </Grid>
      </Grid>
      <StartAnnouncementDialog
        open={openDialog}
        handleClose={handle.handleClose}
        startAnnouncement={handle.startAnnouncement}
        announcementTypeList={announcementTypeList}
        disabled={isAnnouncementStarting}
      />
    </React.Fragment>


  );
}


export default AnnouncementList